import { useEffect, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { type FieldInputProps } from 'formik';
import { Item } from 'react-stately';
import { useFilter } from 'react-aria';
import type { MenuTriggerAction } from '@react-types/combobox';
import type { Key } from '@react-types/shared';
import UncontrolledComboBox, {
  type UncontrolledComboBoxItem,
} from './UncontrolledComboBox';
import type { FormikSetFieldValueTouchedForm } from '../shared/formikTypes';
import { getFieldErrorState } from '../shared/formFieldUi';

const LIST_PAGE_SIZE = 30;

interface FormikControlledComboBoxProps {
  field: FieldInputProps<UncontrolledComboBoxItem | null>;
  form: FormikSetFieldValueTouchedForm<UncontrolledComboBoxItem | null>;
  options: UncontrolledComboBoxItem[];
  labelMsg: string;
  isRequired?: boolean;
}

function FormikControlledComboBox({
  options,
  field,
  form,
  labelMsg,
  isRequired = false,
}: FormikControlledComboBoxProps) {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const { name, value } = field;

  const [inputValue, setInputValue] = useState(value ? value.name : '');
  const [items, setItems] = useState(options);
  const [listCutOff, setListCutOff] = useState(LIST_PAGE_SIZE);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(
    () => () => {
      if (timerRef.current !== null) {
        clearTimeout(timerRef.current);
      }
    },
    []
  );

  const { contains } = useFilter({ sensitivity: 'base' });

  const handleBlur = () => {
    form.setFieldTouched(name, true).catch(() => undefined);
  };

  const handleLoadMore = () => {
    setListCutOff((prevState) => {
      if (prevState + 10 > items.length) {
        return items.length;
      }
      return prevState + 10;
    });
  };

  const onSelectionChange = (key: Key | null) => {
    const selectedItem = items.find((option) => option.id === key);

    setInputValue(selectedItem?.name ?? '');
    setItems(selectedItem ? [selectedItem] : options);
    setListCutOff(LIST_PAGE_SIZE);
    if (selectedItem) {
      form.setFieldValue(name, selectedItem).catch(() => undefined);
    } else {
      form.setFieldValue(name, null).catch(() => undefined);
    }
  };

  const updateSearchResults = (textInputValue: string) => {
    if (!textInputValue) {
      setItems(options);
    } else {
      const newOptions = options.filter((item) =>
        contains(item.name, textInputValue)
      );
      setItems(newOptions);
    }
    setIsLoading(false);
  };

  const delayedSearchResultUpdate = (textInputValue: string) => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
    }
    setIsLoading(true);
    setListCutOff(LIST_PAGE_SIZE);
    timerRef.current = setTimeout(
      () => updateSearchResults(textInputValue),
      500
    );
  };

  const handleKeyPress = (textInputValue: string) => {
    setInputValue(textInputValue);
    setItems([]);
    delayedSearchResultUpdate(textInputValue);
  };

  const onOpenChange = (isOpen: boolean, menuTrigger?: MenuTriggerAction) => {
    if (menuTrigger === 'manual' && isOpen) {
      setItems(options);
    }
  };

  const { isError } = getFieldErrorState(form, name);

  return (
    <UncontrolledComboBox
      name={name}
      label={<FormattedMessage id={labelMsg} />}
      items={items.slice(0, listCutOff)}
      selectedKey={value ? value.id : null}
      inputValue={inputValue}
      onOpenChange={onOpenChange}
      onSelectionChange={onSelectionChange}
      onInputChange={handleKeyPress}
      handleLoadMore={handleLoadMore}
      isLoading={isLoading}
      onBlur={handleBlur}
      isError={isError}
      isRequired={isRequired}
    >
      {(item: UncontrolledComboBoxItem) => <Item>{item.name}</Item>}
    </UncontrolledComboBox>
  );
}

export default FormikControlledComboBox;
export type { FormikControlledComboBoxProps, UncontrolledComboBoxItem };
